import { useState, useMemo } from 'react'
import { RefreshCw, AlertCircle, Gift, Send, CheckCircle2 } from 'lucide-react'
import { useSystemAirdropHistory, useExecuteAirdrop } from '../hooks/useSystemAirdrop'
import { formatDateTime, formatDoge, formatNumber, truncateAddress } from '../lib/utils'

const PAGE_SIZE = 50

export default function SystemAirdrop() {
  const [page, setPage] = useState(0)
  const [amountDoge, setAmountDoge] = useState('')
  const [message, setMessage] = useState('')
  const [showConfirm, setShowConfirm] = useState(false)

  const params = useMemo(
    () => ({
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [page]
  )

  const { data: historyData, loading: historyLoading, error: historyError, refetch } = useSystemAirdropHistory(params)
  const { execute, loading: executeLoading, error: executeError, result: executeResult, reset: resetExecute } = useExecuteAirdrop()

  const handlePageChange = (newPage: number) => {
    if (newPage >= 0) {
      setPage(newPage)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!amountDoge || !message.trim()) return
    setShowConfirm(true)
  }

  const handleConfirmExecute = async () => {
    const amountInShibes = Math.floor(parseFloat(amountDoge) * 100_000_000)
    try {
      await execute({ amount: amountInShibes, message: message.trim() })
      setAmountDoge('')
      setMessage('')
      setShowConfirm(false)
      refetch()
    } catch {
      setShowConfirm(false)
    }
  }

  const handleCancelConfirm = () => {
    setShowConfirm(false)
  }

  const handleDismissResult = () => {
    resetExecute()
  }

  const isFormValid = amountDoge && parseFloat(amountDoge) > 0 && message.trim()

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-3">
            <Gift size={28} className="text-doge-400" />
            System Airdrop
          </h1>
          <p className="text-sm sm:text-base text-slate-400 mt-1">
            Distribute off-chain credits to all users in the system
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={historyLoading}
          className="btn-secondary flex items-center justify-center gap-2 w-full sm:w-auto"
        >
          <RefreshCw size={16} className={historyLoading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Execute Airdrop Form */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-4">Execute Airdrop</h2>

        {/* Success Result */}
        {executeResult && (
          <div className="mb-4 p-4 bg-green-500/10 border border-green-500/50 rounded-lg">
            <div className="flex items-start gap-3">
              <CheckCircle2 size={20} className="text-green-400 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-green-400">Airdrop executed successfully!</p>
                <div className="mt-2 text-sm text-green-400/80 space-y-1">
                  <p>Airdrop ID: <span className="font-mono">{executeResult.airdrop_id}</span></p>
                  <p>Users credited: {formatNumber(executeResult.users_credited)} / {formatNumber(executeResult.total_users_in_userbase)}</p>
                  <p>Users skipped: {formatNumber(executeResult.users_skipped)}</p>
                  <p>Amount per user: {formatDoge(executeResult.amount_per_user)} DOGE</p>
                  <p>Total distributed: {formatDoge(executeResult.total_distributed)} DOGE</p>
                </div>
              </div>
              <button
                onClick={handleDismissResult}
                className="text-green-400 hover:text-green-300 text-sm underline"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Execute Error */}
        {executeError && (
          <div className="mb-4 p-4 bg-red-500/10 border border-red-500/50 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle size={20} className="text-red-400 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-red-400">Error executing airdrop</p>
                <p className="text-sm text-red-400/80 mt-1">
                  {executeError.includes('403') || executeError.includes('Forbidden')
                    ? 'Your wallet is not authorized for system airdrop operations'
                    : executeError}
                </p>
              </div>
              <button
                onClick={resetExecute}
                className="text-red-400 hover:text-red-300 text-sm underline"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="amount" className="block text-sm font-medium text-slate-300 mb-1">
              Amount per user (DOGE)
            </label>
            <input
              type="number"
              id="amount"
              value={amountDoge}
              onChange={(e) => setAmountDoge(e.target.value)}
              min="0.00000001"
              step="0.00000001"
              placeholder="0.00000000"
              className="w-full sm:w-64 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-doge-500 focus:border-transparent"
              disabled={executeLoading}
            />
            <p className="text-xs text-slate-500 mt-1">
              Minimum: 0.00000001 DOGE (1 shibe)
            </p>
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-medium text-slate-300 mb-1">
              Message <span className="text-red-400">*</span>
            </label>
            <textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Enter a message for this airdrop (required)"
              rows={3}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-doge-500 focus:border-transparent resize-none"
              disabled={executeLoading}
            />
          </div>

          <button
            type="submit"
            disabled={!isFormValid || executeLoading}
            className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send size={16} />
            Execute Airdrop
          </button>
        </form>
      </div>

      {/* Confirmation Dialog */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-white mb-4">Confirm Airdrop</h3>
            <div className="space-y-3 text-slate-300 mb-6">
              <p>Are you sure you want to execute this airdrop?</p>
              <div className="bg-slate-800 rounded-lg p-3 space-y-2 text-sm">
                <p><span className="text-slate-400">Amount per user:</span> {amountDoge} DOGE</p>
                <p><span className="text-slate-400">Message:</span> {message}</p>
              </div>
              <p className="text-amber-400 text-sm">
                This action will distribute credits to all eligible users and cannot be undone.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleCancelConfirm}
                className="btn-secondary flex-1"
                disabled={executeLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmExecute}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
                disabled={executeLoading}
              >
                {executeLoading ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    Executing...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Confirm
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Error Alert */}
      {historyError && (
        <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/50 rounded-lg">
          <AlertCircle size={20} className="text-red-400" />
          <div>
            <p className="font-medium text-red-400">Error loading airdrop history</p>
            <p className="text-sm text-red-400/80">
              {historyError.includes('403') || historyError.includes('Forbidden')
                ? 'Your wallet is not authorized for system airdrop operations'
                : historyError}
            </p>
          </div>
          <button
            onClick={() => refetch()}
            className="ml-auto text-sm text-red-400 hover:text-red-300 underline"
          >
            Try again
          </button>
        </div>
      )}

      {/* Airdrop History Table */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-4">Airdrop History</h2>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left border-b border-slate-800">
                <th className="pb-3 text-sm font-medium text-slate-400">Date</th>
                <th className="pb-3 text-sm font-medium text-slate-400">Executor</th>
                <th className="pb-3 text-sm font-medium text-slate-400 text-right">Amount/User</th>
                <th className="pb-3 text-sm font-medium text-slate-400">Message</th>
                <th className="pb-3 text-sm font-medium text-slate-400 text-right">Credited</th>
                <th className="pb-3 text-sm font-medium text-slate-400 text-right">Skipped</th>
                <th className="pb-3 text-sm font-medium text-slate-400 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {historyLoading ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-slate-400">
                    <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
                    Loading...
                  </td>
                </tr>
              ) : !historyData?.records?.length ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-slate-400">
                    No airdrops found
                  </td>
                </tr>
              ) : (
                historyData.records.map((record) => (
                  <tr key={record.id} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                    <td className="py-3 text-sm text-slate-300 whitespace-nowrap">
                      {formatDateTime(record.created_at)}
                    </td>
                    <td className="py-3 text-sm">
                      <span className="inline-flex items-center px-2 py-1 bg-slate-800 rounded text-xs font-mono text-slate-300">
                        {truncateAddress(record.executor_address)}
                      </span>
                    </td>
                    <td className="py-3 text-sm text-slate-300 text-right whitespace-nowrap">
                      {formatDoge(record.amount_per_user)} DOGE
                    </td>
                    <td className="py-3 text-sm text-slate-300 max-w-xs truncate" title={record.message}>
                      {record.message}
                    </td>
                    <td className="py-3 text-sm text-green-400 text-right">
                      {formatNumber(record.users_credited)}
                    </td>
                    <td className="py-3 text-sm text-slate-400 text-right">
                      {formatNumber(record.users_skipped)}
                    </td>
                    <td className="py-3 text-sm text-doge-400 font-medium text-right whitespace-nowrap">
                      {formatDoge(record.total_distributed)} DOGE
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {historyData && historyData.total_count > PAGE_SIZE && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-800">
            <p className="text-sm text-slate-400">
              Showing {page * PAGE_SIZE + 1} - {Math.min((page + 1) * PAGE_SIZE, historyData.total_count)} of {formatNumber(historyData.total_count)}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 0 || historyLoading}
                className="btn-secondary px-3 py-1 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={(page + 1) * PAGE_SIZE >= historyData.total_count || historyLoading}
                className="btn-secondary px-3 py-1 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
